"use client";

import React, { useState } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Loader, ChevronRight, ChevronLeft, CircleCheck, User, FileText, ShieldCheck, Upload } from "lucide-react";
export default function AdvisorApplication() {
  return <section data-section-id="97013" id="verify-your-benefits" className="py-24 bg-background text-foreground relative overflow-hidden">
            {}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-[120px]" />
                <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-[120px]" />
            </div>

            <div className="container mx-auto px-4 relative z-10">
                <div className="max-w-3xl mx-auto text-center mb-12">
                    <h2 className="text-4xl md:text-5xl font-bold text-primary mb-4 tracking-tight font-sans">
                        Verify Your Insurance Coverage
                    </h2>
                    <p className="text-xl text-muted-foreground font-sans">
                        Find out if your medical insurance covers Oral Appliance Therapy. Most major providers offer benefits for sleep apnea treatment.
                    </p>
                </div>

                <div className="max-w-2xl mx-auto">
                    <FormProvider formKey="verify_benefits_form_insurance" formId="verify_benefits_form_insurance" sectionName="verify-your-benefits" defaultValues={{
          full_name: "",
          email: "",
          phone: "",
          city: "",
          experience_years: "",
          resume: "",
          agree_terms: false
        }} validationRules={{
          full_name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true
          },
          city: {
            required: true
          },
          experience_years: {
            required: true,
            min: 0,
            max: 50
          },
          resume: {
            required: true
          },
          agree_terms: {
            required: true
          }
        }} submitText="Verify My Benefits" successMessage="Your verification request has been submitted successfully. Our insurance specialist will review your benefits and contact you within 24 hours.">
                        <WizardForm />
                    </FormProvider>
                </div>
            </div>
        </section>;
}
function WizardForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  const [step, setStep] = useState(1);
  const totalSteps = 3;
  const progress = step / totalSteps * 100;
  const nextStep = () => setStep(s => Math.min(totalSteps, s + 1));
  const prevStep = () => setStep(s => Math.max(1, s - 1));
  if (isSubmitted && !submitError) {
    return <Card className="border-border/50 shadow-2xl shadow-primary/10 text-center py-12 rounded-xl">
                <CardContent className="flex flex-col items-center justify-center space-y-6">
                    <div className="size-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                        <CircleCheck className="size-10 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="space-y-2">
                        <h3 className="text-3xl font-bold text-foreground font-sans">Request Received</h3>
                        <p className="text-lg text-muted-foreground max-w-md mx-auto">
                            {successMessage}
                        </p>
                    </div>
                </CardContent>
            </Card>;
  }
  return <Card className="border-border/50 shadow-2xl shadow-primary/5 overflow-hidden bg-card rounded-xl">
            <Progress value={progress} className="h-2 rounded-none bg-muted" />
            
            <CardHeader className="bg-muted/30 border-b border-border/50 pb-8 pt-8">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-2">
                        <span className="text-xs font-bold text-primary uppercase tracking-widest">
                            Step {step} of {totalSteps}
                        </span>
                    </div>
                    <span className="text-sm text-muted-foreground font-medium italic">
                        {step === 1 && "Patient Contact Info"}
                        {step === 2 && "Insurance Details"}
                        {step === 3 && "Authorization"}
                    </span>
                </div>
                <CardTitle className="text-2xl font-bold text-primary font-sans">Benefit Verification Form</CardTitle>
                <CardDescription className="text-base">
                    Please provide your details to help us determine your coverage eligibility for sleep apnea therapy.
                </CardDescription>
            </CardHeader>

            <CardContent className="pt-8 pb-2">
                <Form onSubmit={handleSubmit}>
                    {}
                    <div className={step === 1 ? "space-y-6 block" : "hidden"}>
                        <div className="flex items-center space-x-3 mb-6 pb-2 border-b border-border/50">
                            <User className="size-5 text-primary" />
                            <h4 className="text-lg font-semibold text-foreground font-sans">Contact Information</h4>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormField control={control} name="full_name" rules={{
              required: "Full Name is required"
            }} render={({
              field
            }) => <FormItem>
                                        <FormLabel className="text-foreground font-medium">Full Name</FormLabel>
                                        <FormControl>
                                            <Input placeholder="John Doe" className="h-12 border-border focus:ring-primary rounded-md" {...field} />
                                        </FormControl>
                                        <FormDescription>Enter your name as it appears on your insurance card.</FormDescription>
                                        <FormMessage />
                                    </FormItem>} />

                            <FormField control={control} name="email" rules={{
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Invalid email address"
              }
            }} render={({
              field
            }) => <FormItem>
                                        <FormLabel className="text-foreground font-medium">Email Address</FormLabel>
                                        <FormControl>
                                            <Input type="email" placeholder="john@example.com" className="h-12 border-border focus:ring-primary rounded-md" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>} />

                            <FormField control={control} name="phone" rules={{
              required: "Phone Number is required"
            }} render={({
              field
            }) => <FormItem>
                                        <FormLabel className="text-foreground font-medium">Phone Number</FormLabel>
                                        <FormControl>
                                            <Input type="tel" placeholder="(555) 000-0000" className="h-12 border-border focus:ring-primary rounded-md" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>} />

                            <FormField control={control} name="city" rules={{
              required: "Insurance Provider is required"
            }} render={({
              field
            }) => <FormItem>
                                        <FormLabel className="text-foreground font-medium">Primary Insurance Provider</FormLabel>
                                        <FormControl>
                                            <Input placeholder="e.g., BlueCross, Aetna, Cigna" className="h-12 border-border focus:ring-primary rounded-md" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>} />
                        </div>
                    </div>

                    {}
                    <div className={step === 2 ? "space-y-6 block" : "hidden"}>
                        <div className="flex items-center space-x-3 mb-6 pb-2 border-b border-border/50">
                            <FileText className="size-5 text-primary" />
                            <h4 className="text-lg font-semibold text-foreground font-sans">Coverage & Symptoms</h4>
                        </div>

                        <div className="space-y-6">
                            <FormField control={control} name="experience_years" rules={{
              required: "Please estimate years of symptoms",
              min: {
                value: 0,
                message: "Cannot be negative"
              },
              max: {
                value: 50,
                message: "Invalid input"
              }
            }} render={({
              field
            }) => <FormItem>
                                        <FormLabel className="text-foreground font-medium">Years Since Symptom Onset</FormLabel>
                                        <FormControl>
                                            <Input type="number" placeholder="2" className="h-12 border-border focus:ring-primary rounded-md" {...field} />
                                        </FormControl>
                                        <FormDescription>How long have you been experiencing snoring or fatigue?</FormDescription>
                                        <FormMessage />
                                    </FormItem>} />

                            <FormField control={control} name="resume" rules={{
              required: "Insurance card image is required"
            }} render={({
              field
            }) => <FormItem>
                                        <FormLabel className="text-foreground font-medium">Upload Insurance Card (Front)</FormLabel>
                                        <FormControl>
                                            <div className="relative">
                                                <Input type="file" accept=".jpg,.jpeg,.png,.pdf" className="h-12 pt-2.5 file:mr-4 file:py-1 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20 cursor-pointer border-border" {...field} />
                                                <Upload className="absolute right-4 top-3.5 size-5 text-muted-foreground pointer-events-none" />
                                            </div>
                                        </FormControl>
                                        <FormDescription>Clear photo of the front of your card (Max 5 MB).</FormDescription>
                                        <FormMessage />
                                    </FormItem>} />
                        </div>
                    </div>

                    {}
                    <div className={step === 3 ? "space-y-6 block" : "hidden"}>
                        <div className="flex items-center space-x-3 mb-6 pb-2 border-b border-border/50">
                            <ShieldCheck className="size-5 text-primary" />
                            <h4 className="text-lg font-semibold text-foreground font-sans">Verification Consent</h4>
                        </div>

                        <div className="bg-muted/30 p-6 rounded-xl border border-border/50 space-y-4">
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                By submitting this form, you authorize Solutions for Sleep Apnea to contact your insurance carrier to verify benefits for Oral Appliance Therapy. We adhere to strict HIPAA compliance standards to protect your private health information.
                            </p>
                            
                            <FormField control={control} name="agree_terms" rules={{
              required: "You must authorize verification to proceed"
            }} render={({
              field
            }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-4">
                                        <FormControl>
                                            <input type="checkbox" className="mt-1 size-5 rounded border-border text-primary focus:ring-primary focus:ring-offset-background bg-background cursor-pointer" checked={field.value} onChange={field.onChange} onBlur={field.onBlur} name={field.name} ref={field.ref} />
                                        </FormControl>
                                        <div className="space-y-1 leading-none">
                                            <FormLabel className="text-sm font-medium leading-relaxed cursor-pointer text-foreground">
                                                I authorize the verification of my medical benefits and confirm the information provided is accurate.
                                            </FormLabel>
                                            <FormMessage />
                                        </div>
                                    </FormItem>} />
                        </div>

                        {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg mt-6">
                                <p className="text-destructive text-sm font-medium">{submitError}</p>
                            </div>}
                        
                        {}
                        <p className="text-xs text-muted-foreground text-center mt-4 italic">
                            If you encounter errors, please check that your insurance provider and contact info are filled correctly in the previous steps.
                        </p>
                    </div>

                    {}
                    <div className="flex items-center justify-between mt-10 pt-6 border-t border-border/50">
                        {step > 1 ? <Button type="button" variant="outline" onClick={prevStep} className="h-12 px-6 border-primary text-primary hover:bg-primary/5 rounded-md">
                                <ChevronLeft className="mr-2 size-4" />
                                Back
                            </Button> : <div></div>}

                        {step < totalSteps ? <Button type="button" onClick={nextStep} className="h-12 px-8 bg-primary text-primary-foreground hover:opacity-90 rounded-md">
                                Next Step
                                <ChevronRight className="ml-2 size-4" />
                            </Button> : <Button type="submit" disabled={isSubmitting} className="h-12 px-8 bg-green-600 text-white hover:bg-green-700 rounded-md shadow-lg transition-all">
                                {isSubmitting ? <>
                                        <Loader className="animate-spin mr-2 size-4" />
                                        Verifying...
                                    </> : <>
                                        Verify My Coverage
                                        <CircleCheck className="ml-2 size-4" />
                                    </>}
                            </Button>}
                    </div>
                </Form>
            </CardContent>
        </Card>;
}